import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { api, ApiError } from "../lib/api";
import { useToast } from "../components/Toast";

/**
 * Einstellungen › Händler (19.10.2026). Ziel: ein Name je Händler und die Rechtsform (Firmenname) nur EINMAL festlegen.
 * - Rechtsform gilt für eine Firma: am sichersten über die USt-IdNr. vom Bon (gehört zur Firma); ohne Nummer über Marke + Filiale.
 * - Was auf Belegen schon steht, ist nur ein Vorschlag (kann geraten oder falsch gelesen sein) — erst „speichern“ macht es verbindlich.
 * - Neue Belege übernehmen die Rechtsform automatisch; vorhandene nur über „auf Belege übertragen“ (mit Vorschau).
 */
type Stamm = { id: string; ustId: string | null; marke: string; filiale: string | null; rechtsform: string };
type Haendler = { schluessel: string; marke: string; anzahl: number; schreibweiseFest: boolean; ohneRechtsform: number; schreibweisen: { name: string; anzahl: number }[];
  filialen: { text: string; anzahl: number }[]; rechtsformenGesehen: { name: string; anzahl: number }[]; ustIds: { nr: string; anzahl: number }[]; stamm: string[] };
type Alias = { id: string; schreibweise: string; marke: string; art: "schreibweise" | "firma" };
type Antwort = { haendler: Haendler[]; stamm: Stamm[]; aliase: Alias[]; gleicheFirma: { nr: string; marken: string[] }[] };
type Vorschau = { passend: number; schonGleich: number; uebersprungen: number; neu: number; ausgefuehrt: boolean };

const feld = "border border-ios-line rounded-lg px-2 py-1.5 text-[16px] lg:text-[14px] bg-white";
const fehlertext = (e: unknown) => (e instanceof ApiError ? e.message : "Aktion fehlgeschlagen");

export function HaendlerPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery<Antwort>({ queryKey: ["haendler"], queryFn: () => api.get("/api/haendler") });
  const neu = () => void qc.invalidateQueries({ queryKey: ["haendler"] });
  const alias = useMutation({
    mutationFn: (b: { schreibweise: string; marke: string; art: string }) => api.post("/api/haendler/aliase", b),
    onSuccess: () => { toast.success("Schreibweise gespeichert — gilt für neue Belege"); neu(); }, onError: (e) => toast.error(fehlertext(e)),
  });
  const aliasWeg = useMutation({ mutationFn: (id: string) => api.del(`/api/haendler/aliase/${id}`), onSuccess: neu });
  const stammVon = new Map((data?.stamm ?? []).map((s) => [s.id, s]));

  return (
    <div className="max-w-2xl mx-auto p-4 lg:p-6">
      <Link to="/einstellungen" className="inline-flex items-center gap-1 text-ios-blue text-[14px] mb-3"><ChevronLeftIcon className="w-4 h-4" /> Einstellungen</Link>
      <h1 className="text-[22px] font-semibold tracking-tight mb-1">Händler</h1>
      <p className="text-[13px] text-ios-muted mb-4">
        Die Rechtsform (Firmenname) brauchst du für geschäftliche und Vermietungs-Belege. Lege sie hier einmal je Firma fest — neue Belege
        übernehmen sie dann automatisch. Was auf Belegen schon steht, ist nur ein Vorschlag: es kann von der Texterkennung falsch gelesen oder geraten sein.
      </p>
      {isLoading || !data ? <p className="text-[14px] text-ios-muted">Lädt …</p> : (
        <div className="space-y-3">
          {data.gleicheFirma.map((g) => (
            <div key={g.nr} className="bg-ios-blue/10 rounded-iosLg p-3 text-[13px] space-y-1">
              <div>Dieselbe USt-IdNr. {g.nr} steht unter {g.marken.map((m) => `„${m}“`).join(" und ")} — vermutlich ein Händler.</div>
              <div className="flex flex-wrap gap-2">{g.marken.map((ziel) => (
                <button key={ziel} type="button" className="text-ios-blue" disabled={alias.isPending}
                  onClick={() => g.marken.filter((m) => m !== ziel).forEach((m) => alias.mutate({ schreibweise: m, marke: ziel, art: "schreibweise" }))}>
                  alle als „{ziel}“ führen</button>))}</div>
            </div>
          ))}
          {data.haendler.map((h) => <HaendlerKarte key={h.schluessel} h={h} stamm={h.stamm.map((id) => stammVon.get(id)!).filter(Boolean)}
            onAlias={(b) => alias.mutate(b)} onNeu={neu} />)}
          {data.aliase.length > 0 && (
            <section className="bg-white rounded-iosLg shadow-card p-4 text-[13px] space-y-1">
              <h2 className="text-[15px] font-medium mb-1">Schreibweisen</h2>
              {data.aliase.map((a) => (
                <div key={a.id} className="flex gap-2 items-baseline">
                  <span className="flex-1">„{a.schreibweise}“ → {a.marke} <span className="text-ios-muted">({a.art === "firma" ? "Firmenname auf dem Bon" : "nur Schreibweise"})</span></span>
                  <button type="button" className="text-ios-blue" onClick={() => aliasWeg.mutate(a.id)}>entfernen</button>
                </div>
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function HaendlerKarte({ h, stamm, onAlias, onNeu }: { h: Haendler; stamm: Stamm[]; onAlias: (b: { schreibweise: string; marke: string; art: string }) => void; onNeu: () => void }) {
  const [offen, setOffen] = useState(false);
  return (
    <div className="bg-white rounded-iosLg shadow-card p-4 space-y-2 text-[13px]">
      <div className="flex items-baseline gap-2">
        <span className="text-[16px] font-medium flex-1">{h.marke}</span>
        <span className="text-ios-muted">{h.anzahl} Belege{h.ohneRechtsform ? ` · ${h.ohneRechtsform} ohne Rechtsform` : ""}</span>
      </div>
      {h.schreibweisen.length > 1 && !h.schreibweiseFest && (
        <div className="text-ios-blue">Schreibweisen: {h.schreibweisen.map((s) => `${s.name} (${s.anzahl})`).join(", ")} — einheitlich als{" "}
          {h.schreibweisen.map((s) => <button key={s.name} type="button" className="underline mr-2" onClick={() => onAlias({ schreibweise: s.name, marke: s.name, art: "schreibweise" })}>{s.name}</button>)}
        </div>
      )}
      {h.filialen.length > 0 && <div className="text-ios-muted">Filialen: {h.filialen.map((f) => `${f.text} (${f.anzahl})`).join(" · ")}</div>}
      {h.ustIds.length > 0 && <div className="text-ios-muted">USt-IdNr. auf Belegen: {h.ustIds.map((u) => `${u.nr} (${u.anzahl})`).join(", ")}</div>}
      {stamm.map((s) => <StammZeile key={s.id} s={s} onNeu={onNeu} />)}
      {stamm.length === 0 && <div className="text-ios-blue">Rechtsform noch nicht festgelegt.</div>}
      <button type="button" className="text-ios-blue" onClick={() => setOffen(!offen)}>{offen ? "schließen" : "+ Rechtsform festlegen"}</button>
      {offen && <StammForm h={h} onFertig={() => { setOffen(false); onNeu(); }} />}
    </div>
  );
}

function StammForm({ h, onFertig }: { h: Haendler; onFertig: () => void }) {
  const toast = useToast();
  const [rechtsform, setRechtsform] = useState(""), [ustId, setUstId] = useState(h.ustIds.length === 1 ? h.ustIds[0]!.nr : ""), [filiale, setFiliale] = useState("");
  const speichern = useMutation({
    mutationFn: () => api.post("/api/haendler/stamm", { marke: h.marke, rechtsform, ustId: ustId || undefined, filiale: ustId ? undefined : filiale || undefined }),
    onSuccess: () => { toast.success("Rechtsform gespeichert — gilt für neue Belege"); onFertig(); }, onError: (e) => toast.error(fehlertext(e)),
  });
  return (
    <div className="bg-ios-bg/60 rounded-lg p-3 space-y-2">
      <input className={`${feld} w-full`} placeholder="Firmenname mit Rechtsform, z. B. Muster GmbH" value={rechtsform} onChange={(e) => setRechtsform(e.target.value)} />
      {h.rechtsformenGesehen.length > 0 && (
        <div className="text-ios-muted">Auf Belegen gesehen (ungeprüft, zum Übernehmen antippen):{" "}
          {h.rechtsformenGesehen.map((r) => <button key={r.name} type="button" className="underline mr-2 text-ios-blue" onClick={() => setRechtsform(r.name)}>{r.name} ({r.anzahl})</button>)}
        </div>
      )}
      <label className="block">Gilt für Firma mit USt-IdNr.{" "}
        <input className={feld} value={ustId} placeholder="DE…" onChange={(e) => setUstId(e.target.value.toUpperCase())} list={`ust-${h.schluessel}`} />
        <datalist id={`ust-${h.schluessel}`}>{h.ustIds.map((u) => <option key={u.nr} value={u.nr} />)}</datalist>
      </label>
      {!ustId && (
        <label className="block">ohne Nummer: gilt für{" "}
          <select className={feld} value={filiale} onChange={(e) => setFiliale(e.target.value)}>
            <option value="">alle Filialen von {h.marke}</option>
            {h.filialen.map((f) => <option key={f.text} value={f.text}>nur {f.text}</option>)}
          </select>
        </label>
      )}
      <button type="button" disabled={!rechtsform.trim() || speichern.isPending} onClick={() => speichern.mutate()}
        className="px-3 py-1.5 rounded-ios bg-ios-blue text-white disabled:opacity-40">speichern</button>
    </div>
  );
}

function StammZeile({ s, onNeu }: { s: Stamm; onNeu: () => void }) {
  const toast = useToast();
  const [v, setV] = useState<Vorschau | null>(null);
  const zaehlen = useMutation({ mutationFn: () => api.post<Vorschau>(`/api/haendler/stamm/${s.id}/uebertragen`), onSuccess: setV, onError: (e) => toast.error(fehlertext(e)) });
  const ausfuehren = useMutation({
    mutationFn: () => api.post<Vorschau>(`/api/haendler/stamm/${s.id}/uebertragen?ausfuehren=1`),
    onSuccess: (r) => { toast.success(`${r.neu} Belege übernommen`); setV(null); onNeu(); }, onError: (e) => toast.error(fehlertext(e)),
  });
  const weg = useMutation({ mutationFn: () => api.del(`/api/haendler/stamm/${s.id}`), onSuccess: onNeu });
  return (
    <div className="border-t border-ios-line pt-2 space-y-1">
      <div className="flex gap-2 items-baseline">
        <span className="flex-1"><span className="text-ios-green">✓</span> <b>{s.rechtsform}</b>{" "}
          <span className="text-ios-muted">{s.ustId ? `USt-IdNr. ${s.ustId}` : s.filiale ? `nur Filiale ${s.filiale}` : "alle Filialen"}</span></span>
        <button type="button" className="text-ios-blue" onClick={() => weg.mutate()}>entfernen</button>
      </div>
      {!v && <button type="button" className="text-ios-blue" onClick={() => zaehlen.mutate()}>auf vorhandene Belege übertragen …</button>}
      {v && (
        <div className="bg-ios-bg/60 rounded-lg p-2">
          {v.passend} Belege passen: {v.neu} würden geändert, {v.schonGleich} stimmen schon
          {v.uebersprungen ? `, ${v.uebersprungen} bleiben (Rechtsform dort von Hand gesetzt)` : ""}.{" "}
          <button type="button" disabled={v.neu === 0 || ausfuehren.isPending} className="text-ios-blue disabled:opacity-40" onClick={() => ausfuehren.mutate()}>übertragen</button>
        </div>
      )}
    </div>
  );
}
